import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { TableEditColumn } from '@devexpress/dx-react-grid-material-ui';

import PropTypes from 'prop-types';

/**
 * KebabCommandCell
 *
 * A custom cellComponent for TableEditColumn that collapses Add line, Edit and Delete
 * command buttons into a kebab (⋮) dropdown menu.
 *
 * The grid passes already-wired command buttons as `children`. Each child has
 * props: { id: 'edit' | 'delete' | ..., onExecute: () => void }.
 * We extract those onExecute callbacks and hook them into the menu items.
 */
const KebabCommandCell = ({ children, onPopupEditStart, onAddLine, onValidateNewRow, row, ...rest }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  // Extract onExecute callbacks from the grid-provided command children.
  // Each child is a rendered command component with props: { id, onExecute }
  const commandMap = {};
  React.Children.forEach(children, (child) => {
    if (child && child.props && child.props.id) {
      commandMap[child.props.id] = child.props.onExecute;
    }
  });

  // New rows use Save/Cancel commands instead of the row-actions menu.
  if (commandMap['commit'] || commandMap['cancel']) {
    const newRow = !row?.invoiceId && !row?._id;
    const commands = React.Children.map(children, (child) => {
      if (!newRow || child?.props?.id !== 'commit') return child;
      return React.cloneElement(child, {
        onExecute: () => {
          if (onValidateNewRow?.(row)) child.props.onExecute();
        }
      });
    });
    return (
      <TableEditColumn.Cell {...rest} row={row}>
        {commands}
      </TableEditColumn.Cell>
    );
  }

  const handleEdit = (event) => {
    event.stopPropagation();
    if (commandMap['edit']) {
      onPopupEditStart();
      commandMap['edit']();
    }
    handleClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (commandMap['delete']) {
      commandMap['delete']();
    }
    handleClose();
  };

  const handleAddLine = (event) => {
    event.stopPropagation();
    handleClose();
    onAddLine?.(row);
  };

  // Render an empty command cell if no actions are available.
  const hasActions = (onAddLine && (row?.invoiceId || row?._id)) || commandMap['edit'] || commandMap['delete'];
  if (!hasActions) {
    return <td {...rest} />;
  }

  return (
    <td
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      style={{
        borderBottom: '1px solid #e0e0e0',
        padding: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <IconButton aria-label="row actions" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {onAddLine && (
          <MenuItem onClick={handleAddLine}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add new line</ListItemText>
          </MenuItem>
        )}
        {commandMap['edit'] && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {commandMap['delete'] && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </td>
  );
};

export default KebabCommandCell;

KebabCommandCell.propTypes = {
  children: PropTypes.any,
  onPopupEditStart: PropTypes.func,
  onAddLine: PropTypes.func,
  onValidateNewRow: PropTypes.func,
  row: PropTypes.object
};

KebabCommandCell.defaultProps = { onPopupEditStart: () => {} };
